import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, TextField, Button, Typography, Alert, CircularProgress, Link } from '@mui/material';
import clientApi from '../services/clientApi';
import logo from '../assets/re-stat-logo.svg';

function Login() {
  const [mode, setMode] = useState<'login' | 'forgot'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await clientApi.login(email, password);
      navigate('/search');
    } catch (err: any) {
      setError(err.message || 'Login fallito. Verifica le credenziali.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);
    try {
      const res = await clientApi.forgotPassword(email);
      setInfo(res.message || 'Se l\'email è associata a un account, riceverai un link per reimpostare la password.');
    } catch (err: any) {
      // Backend responds generically anyway; show a neutral message.
      setInfo('Se l\'email è associata a un account, riceverai un link per reimpostare la password.');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (m: 'login' | 'forgot') => {
    setMode(m); setError(''); setInfo('');
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, bgcolor: 'background.default' }}>
      <Paper elevation={3} sx={{ p: 4, width: '100%', maxWidth: 420 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box component="img" src={logo} alt="re-stat" sx={{ height: 60, mb: 1 }} />
          <Typography color="text.secondary">
            {mode === 'login' ? 'Ricerca Comparabili' : 'Recupera password'}
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {info && <Alert severity="success" sx={{ mb: 2 }}>{info}</Alert>}

        {mode === 'login' ? (
          <form onSubmit={handleLogin}>
            <TextField label="Email" type="email" fullWidth required autoFocus margin="normal"
              value={email} onChange={(e) => setEmail(e.target.value)} disabled={loading} />
            <TextField label="Password" type="password" fullWidth required margin="normal"
              value={password} onChange={(e) => setPassword(e.target.value)} disabled={loading} />
            <Box sx={{ textAlign: 'right', mt: 0.5 }}>
              <Link component="button" type="button" variant="body2" underline="hover"
                onClick={() => switchMode('forgot')}>
                Password dimenticata?
              </Link>
            </Box>
            <Button type="submit" variant="contained" fullWidth size="large" sx={{ mt: 2 }} disabled={loading}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : undefined}>
              {loading ? 'Accesso in corso…' : 'Accedi'}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleForgot}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Inserisci l'email del tuo account: ti invieremo un link per reimpostare la password.
            </Typography>
            <TextField label="Email" type="email" fullWidth required autoFocus margin="normal"
              value={email} onChange={(e) => setEmail(e.target.value)} disabled={loading} />
            <Button type="submit" variant="contained" fullWidth size="large" sx={{ mt: 2 }} disabled={loading}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : undefined}>
              {loading ? 'Invio…' : 'Invia link di reset'}
            </Button>
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link component="button" type="button" variant="body2" underline="hover"
                onClick={() => switchMode('login')}>
                ← Torna al login
              </Link>
            </Box>
          </form>
        )}
      </Paper>
    </Box>
  );
}

export default Login;
